<script setup lang="ts">
/**
 * Enkelvoudig tekstveld: text, email, password, number, tel, url, time, …
 *
 * Vervangt `<v-input type="text|email|number|…">`. Met `display-as` toont hetzelfde
 * veld de waarde in leesmodus (VInput: `display-only-value`).
 */
import type { MaskInputOptions } from 'maska'
import { vMaska } from 'maska/vue'
import { computed, ref, useSlots } from 'vue'
import VCopyButton from '../VCopyButton.vue'
import VDisplayValue from './VDisplayValue.vue'
import VFormField from './VFormField.vue'
import type { DisplayableProps, FormFieldBaseProps, TextInputType } from './types'
import { controlClasses, useFieldErrors, useFormFieldBindings, useSplitAttrs } from './useFormField'

defineOptions({ inheritAttrs: false })

const modelValue = defineModel<string | number | null>()

const props = withDefaults(
    defineProps<
        FormFieldBaseProps &
            DisplayableProps & {
                type?: TextInputType
                placeholder?: string | null
                mask?: MaskInputOptions | null
                /**
                 * EU-decimaal: toont en bewaart een komma i.p.v. een punt. Het model bevat dan
                 * een string ("12,5"); de server moet die zelf normaliseren.
                 */
                euNumber?: boolean
                min?: number | string | null
                max?: number | string | null
                /** Meerdere e-mailadressen in één veld (`type="email"`). */
                multiple?: boolean
                autocomplete?: string
                /** Btw-toggle (incl./excl.) in het label. */
                vatButton?: boolean
                vatValue?: boolean
            }
    >(),
    {
        type: 'text',
        placeholder: null,
        mask: null,
        euNumber: false,
        min: null,
        max: null,
        multiple: false,
        autocomplete: 'on',
        displayAs: null,
        vatButton: false,
        vatValue: false,
        showErrors: true,
    }
)

const emit = defineEmits<{
    (e: 'vatChanges', value: 'vat_included' | 'vat_excluded'): void
}>()

const slots = useSlots()
const { rootAttrs, controlAttrs } = useSplitAttrs()
const errors = useFieldErrors(props)
const fieldBindings = useFormFieldBindings(props, errors)

const inputRef = ref<HTMLInputElement | null>(null)

function focus(): void {
    inputRef.value?.focus()
}

defineExpose({ inputRef, focus })

const classes = computed<string>(() =>
    controlClasses({
        inputClasses: props.inputClasses,
        invalid: props.invalid,
        hasPrefix: !!slots.prefix,
        hasSuffix: !!slots.suffix,
    })
)

/**
 * EU-getalweergave: toont een komma en zet ingetypte punten om naar komma's.
 * Zonder `euNumber` een gewone passthrough.
 *
 * VInput verving enkel de eerste punt (`replace('.', ',')`); "1.000.5" werd "1,000.5".
 */
const inputValue = computed({
    get: () => {
        if (!props.euNumber || modelValue.value === null || modelValue.value === undefined) {
            return modelValue.value
        }

        return String(modelValue.value).replaceAll('.', ',')
    },
    set: (value: string | number | null | undefined) => {
        if (props.euNumber && typeof value === 'string') {
            modelValue.value = value.replaceAll('.', ',')
        } else {
            modelValue.value = value
        }
    },
})

/**
 * Kopieerknop in een ingevuld e-mailveld. Herkend op type="email" of op de naam: veel
 * e-mailvelden zijn gewone tekstvelden (name="email", "accountant_outgoing_email", …).
 */
// Enkel velden waarvan de naam op "email" eindigt (email, contact_email, users.0.e-mail):
// /e-?mail/ ving ook email.subject en email.sender_name in de Peppol-instellingen.
const isEmailField = computed(
    () => props.type === 'email' || /(^|[._-])e-?mail$/i.test(props.name ?? '')
)
const showCopy = computed(
    () => isEmailField.value && !!modelValue.value && String(modelValue.value).trim() !== ''
)
</script>

<template>
    <VDisplayValue
        v-if="displayAs != null"
        v-bind="$attrs"
        :value="modelValue"
        :type="displayAs"
        :layout="layout"
        :align-right="inputClasses?.includes('text-right') ?? false"
    >
        <template v-if="$slots.suffix" #suffix><slot name="suffix"></slot></template>
        <template v-if="$slots.hint" #hint><slot name="hint"></slot></template>
    </VDisplayValue>

    <VFormField
        v-else
        v-bind="{ ...fieldBindings, ...rootAttrs }"
        :vat-button="vatButton"
        :vat-value="vatValue"
        @vat-changes="emit('vatChanges', $event)"
    >
        <template v-if="$slots.prefix" #prefix><slot name="prefix"></slot></template>

        <input
            v-bind="controlAttrs"
            ref="inputRef"
            v-model="inputValue"
            v-maska="mask ?? undefined"
            :multiple="multiple"
            :min="min ?? undefined"
            :max="max ?? undefined"
            :id="id ?? name ?? undefined"
            :name="name ?? undefined"
            :type="type"
            :placeholder="placeholder ?? undefined"
            :class="[classes, showCopy && '!pr-8']"
            :disabled="disabled"
            :required="required"
            :autocomplete="autocomplete"
        />
        <!-- Kopieerknop achteraan in het e-mailveld (quality of life, vraag klant
             25/09/2026): over de rechterrand van het veld, zonder grijs suffixvak. -->
        <VCopyButton v-if="showCopy" :value="inputValue" class="z-10 -ml-7 w-7 self-center" />

        <template v-if="$slots.suffix" #suffix><slot name="suffix"></slot></template>
        <template v-if="$slots.hint" #hint><slot name="hint"></slot></template>
    </VFormField>
</template>
